/**
 * CacheManager — pure key-value cache store with time-awareness.
 *
 * This is a PASSIVE store. It never triggers fetches — it only stores,
 * retrieves, evicts, and notifies. The caller is responsible for fetching
 * data (via ActionRegistry, direct HTTP, etc.) and then calling `set()`.
 *
 * Features:
 * - LRU eviction with configurable maxEntries (default 200)
 * - Stale-while-revalidate: get() returns { data, status } so callers
 *   can serve stale data immediately while revalidating in background
 * - Lazy GC: no setInterval — expired entries purged on every N-th set()
 * - Event-driven invalidation via subscribe/notify
 * - O(1) prefix invalidation via secondary index
 */

import type { CachePolicy } from './CachePolicy';
import { DEFAULT_POLICY } from './CachePolicy';

export interface CacheResult<T> {
  data: T | undefined;
  status: 'fresh' | 'stale' | 'miss';
}

interface CacheEntry {
  data: unknown;
  fetchedAt: number;
  accessedAt: number;
  policy: CachePolicy;
}

interface CacheManagerOptions {
  maxEntries?: number;
  defaultPolicy?: Partial<CachePolicy>;
  gcEveryNWrites?: number;
}

type InvalidationCallback = (key: string) => void;

interface Subscription {
  pattern: string | RegExp;
  callback: InvalidationCallback;
}

export class CacheManager {
  private store = new Map<string, CacheEntry>();
  private prefixIndex = new Map<string, Set<string>>();
  private subscriptions: Subscription[] = [];
  private writeCount = 0;

  private readonly maxEntries: number;
  private readonly defaultPolicy: CachePolicy;
  private readonly gcEveryNWrites: number;

  constructor(options: CacheManagerOptions = {}) {
    this.maxEntries = options.maxEntries ?? 200;
    this.defaultPolicy = { ...DEFAULT_POLICY, ...options.defaultPolicy };
    this.gcEveryNWrites = options.gcEveryNWrites ?? 50;
  }

  /**
   * Retrieve a cached entry. Returns status so the caller can decide:
   * - 'fresh': data is within staleTime, safe to use as-is
   * - 'stale': data exists but is past staleTime — serve it and revalidate
   * - 'miss': no data available
   */
  get<T = unknown>(key: string): CacheResult<T> {
    const entry = this.store.get(key);
    if (!entry) return { data: undefined, status: 'miss' };

    entry.accessedAt = Date.now();
    this.promoteToRecent(key);

    const age = Date.now() - entry.fetchedAt;
    if (entry.policy.staleTime === Infinity || age < entry.policy.staleTime) {
      return { data: entry.data as T, status: 'fresh' };
    }

    if (entry.policy.gcTime === Infinity || age < entry.policy.gcTime) {
      return { data: entry.data as T, status: 'stale' };
    }

    this.removeEntry(key);
    return { data: undefined, status: 'miss' };
  }

  /**
   * Store data in the cache. Triggers LRU eviction if at capacity.
   * Runs lazy GC every N writes to purge expired entries.
   */
  set<T = unknown>(key: string, data: T, policy?: Partial<CachePolicy>): void {
    const resolved: CachePolicy = { ...this.defaultPolicy, ...policy };
    const now = Date.now();

    this.store.set(key, { data, fetchedAt: now, accessedAt: now, policy: resolved });
    this.indexKey(key);
    this.promoteToRecent(key);

    this.writeCount++;
    if (this.writeCount >= this.gcEveryNWrites) {
      this.writeCount = 0;
      this.lazyGC();
    }

    while (this.store.size > this.maxEntries) {
      this.evictLRU();
    }
  }

  /** Check if a fresh entry exists for the given key. */
  has(key: string): boolean {
    const result = this.get(key);
    return result.status === 'fresh';
  }

  /** Remove a specific key from the cache and notify subscribers. */
  invalidate(key: string): void {
    this.removeEntry(key);
    this.notifySubscribers(key);
  }

  /** Remove all keys that share a prefix (the portion before `::`). */
  invalidateByPrefix(prefix: string): void {
    const keys = this.prefixIndex.get(prefix);
    if (keys) {
      for (const key of keys) {
        this.store.delete(key);
      }
      keys.clear();
    }
    this.store.delete(prefix);
    this.notifySubscribers(prefix);
  }

  /** Remove all entries. */
  clear(): void {
    this.store.clear();
    this.prefixIndex.clear();
  }

  /** Release all resources. After calling, the instance should not be reused. */
  destroy(): void {
    this.clear();
    this.subscriptions = [];
  }

  /** Number of entries currently in the cache. */
  get size(): number {
    return this.store.size;
  }

  /**
   * Subscribe to invalidation events. The callback fires whenever a matching
   * key is invalidated (via invalidate() or invalidateByPrefix()).
   *
   * @param pattern - exact string match or RegExp tested against the invalidated key
   * @returns unsubscribe function
   */
  subscribe(pattern: string | RegExp, callback: InvalidationCallback): () => void {
    const sub: Subscription = { pattern, callback };
    this.subscriptions.push(sub);
    return () => {
      const idx = this.subscriptions.indexOf(sub);
      if (idx !== -1) this.subscriptions.splice(idx, 1);
    };
  }

  // --- Private helpers ---

  private indexKey(key: string): void {
    const separatorIdx = key.indexOf('::');
    const prefix = separatorIdx === -1 ? key : key.slice(0, separatorIdx);

    if (prefix === key) return;

    let keySet = this.prefixIndex.get(prefix);
    if (!keySet) {
      keySet = new Set();
      this.prefixIndex.set(prefix, keySet);
    }
    keySet.add(key);
  }

  private removeEntry(key: string): void {
    this.store.delete(key);
    const separatorIdx = key.indexOf('::');
    if (separatorIdx !== -1) {
      const prefix = key.slice(0, separatorIdx);
      this.prefixIndex.get(prefix)?.delete(key);
    }
  }

  /**
   * LRU eviction via Map iteration order.
   * Map preserves insertion order — we re-insert on access (promoteToRecent),
   * so the first entry in iteration is the least recently used.
   */
  private evictLRU(): void {
    const firstKey = this.store.keys().next().value;
    if (firstKey != null) {
      this.removeEntry(firstKey);
    }
  }

  /** Re-insert entry to move it to the end of Map (most recently used). */
  private promoteToRecent(key: string): void {
    const entry = this.store.get(key);
    if (entry) {
      this.store.delete(key);
      this.store.set(key, entry);
    }
  }

  /** Lazy GC: purge entries past their gcTime. Runs on every N-th write. */
  private lazyGC(): void {
    const now = Date.now();
    for (const [key, entry] of this.store) {
      if (entry.policy.gcTime !== Infinity && now - entry.accessedAt > entry.policy.gcTime) {
        this.removeEntry(key);
      }
    }
  }

  private notifySubscribers(key: string): void {
    for (const sub of this.subscriptions) {
      const matches =
        typeof sub.pattern === 'string'
          ? key === sub.pattern || key.startsWith(`${sub.pattern}::`)
          : sub.pattern.test(key);
      if (matches) {
        try {
          sub.callback(key);
        } catch {
          // Subscriber errors must not break cache operations
        }
      }
    }
  }
}

/**
 * Global singleton — shared across all engines in the app.
 *
 * Use this for lookups that should be shared (countries, states, plans, etc.).
 * For isolated per-component caching, create a new CacheManager instance.
 */
export const globalCache = new CacheManager();
